import { useEffect, useRef, useState } from 'react'
import { Mail, Lock, ArrowRight, AlertCircle } from 'react-feather'
import { AuthProvider, useAuth } from './auth-context'
import DashboardShell from './components/DashboardShell'
import ExchangeViewer from './components/ExchangeViewer'
import { readExchangeView } from './lib/exchanges'
import { firstViewReady, releaseBootLoader } from './lib/bootLoader'

function AppShell() {
  const { user, loading } = useAuth()
  // Capture before ordinary chat hydration normalizes URL query parameters.
  const [exchangeView] = useState(() => readExchangeView(window.location.href))

  // Single-loader handoff: the inline boot loader (rendered before React
  // hydrates — see index.html) stays visible until the first real view is on
  // screen: the sign-in screen or exchange viewer as soon as auth resolves, the
  // dashboard once its chat list and restored chat have loaded (lib/bootLoader).
  useEffect(() => {
    if (loading) return
    if (!user || exchangeView || firstViewReady()) { releaseBootLoader(); return }
    const onReady = () => releaseBootLoader()
    window.addEventListener('rf:first-view', onReady, { once: true })
    // A failed/slow bootstrap must never keep the loader up: the dashboard shows its own states.
    const fallback = window.setTimeout(onReady, 8_000)
    return () => { window.removeEventListener('rf:first-view', onReady); window.clearTimeout(fallback) }
  }, [loading, user, exchangeView])

  // While auth resolves, render nothing — the inline boot loader still
  // owns the screen. Returning null is critical: rendering an empty
  // background here would briefly cover the boot loader.
  if (loading) return null
  if (!user) return <SignInScreen />
  if (exchangeView) return <ExchangeViewer {...exchangeView} />
  return <DashboardShell />
}

/* =========================================================================
   Sign-in — ported from Reputifly API index.html (unchanged)
   ========================================================================= */
function SignInScreen() {
  const { signInWithEmail } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // Lock body scroll on iOS for the sign-in screen
  useEffect(() => {
    document.body.classList.add('no-scroll')
    return () => document.body.classList.remove('no-scroll')
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await signInWithEmail(email.trim(), password)
      // onAuthStateChanged handles the rest
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? ''
      let msg = 'Invalid credentials.'
      if (code === 'auth/user-not-found') msg = 'User not found.'
      else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') msg = 'Incorrect email or password.'
      else if (code === 'auth/too-many-requests') msg = 'Too many attempts. Wait a minute, try again.'
      else if (code === 'auth/invalid-email') msg = 'Invalid email format.'
      else if (code === 'auth/network-request-failed') msg = 'Network error. Check connection.'
      setError(msg)
      setLoading(false)
      // shake animation
      if (cardRef.current) {
        cardRef.current.classList.remove('shake')
        // trigger reflow then re-add
        void cardRef.current.offsetWidth
        cardRef.current.classList.add('shake')
      }
    }
  }

  return (
    <>
      <div className="ambient-glow ambient-glow--pink" />
      <div className="ambient-glow ambient-glow--orange" />

      <div className="min-h-screen w-screen flex items-center justify-center px-4 relative">
        <div ref={cardRef} className="rainbow-p w-full max-w-sm">
          <div className="glass-inner p-8 relative">
            <div className="text-center mb-8 mt-2">
              <img
                src="https://reputifly.com/wp-content/uploads/2025/05/cropped-reputifly-new-e1746390492876.png"
                alt="Reputifly"
                className="h-10 mx-auto mb-4 brightness-125 object-contain"
              />
              <h1 className="text-2xl font-bold brand-font text-white">Workspace Login</h1>
              <p className="text-sm text-gray-500 mt-2">Enter credentials to log in your account.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-wider ml-1">
                  Email Identity
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    className="input-glass"
                    placeholder="name@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={loading}
                  />
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-wider ml-1">
                  Secure Key
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type="password"
                    className="input-glass"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    disabled={loading}
                  />
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
              </div>

              <div className="pt-4">
                <button type="submit" disabled={loading} className="btn-liquid group">
                  {loading ? (
                    <>
                      <span className="spinner" />
                      <span>Verifying…</span>
                    </>
                  ) : (
                    <>
                      <span>Authenticate</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </div>

              {error && (
                <p className="text-red-400 text-xs text-center mt-4 bg-red-500/10 border border-red-500/20 p-2 rounded-lg flex items-center justify-center gap-2">
                  <AlertCircle className="w-3 h-3" />
                  <span>{error}</span>
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  )
}
